import { useState, useEffect } from 'react';
import request from '../../utils/request';
import loader from '../common/loader.gif';

import {
  Tooltip,
  Divider,
  Box,
  Card,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableContainer,
  Typography,
  useTheme,
  Grid,
  Switch,
  TextField,
  Autocomplete,
  Chip
} from '@mui/material';

import DeleteAlert from './DeleteAlert';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';

import SnackbarAlert from '../common/SnackbarAlert';
import { checkPermission } from '../../utils/permission';
import PageHeader from '../common/PageHeader';
import ShowHistoryButton from '../common/ShowHistoryButton';
import NoDataAvailable from '../common/NoDataAvailable';
import TableSearchWithIcon from '../common/TableSearchWithIcon';

const DocumentNameList = ({
  editDatas,
  showCreateEditForm
}) => {
  const theme = useTheme();

  const [isLoading, setLoading] = useState(true);

  const [offset, setOffset] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [documentNameList, setDocumentNameList] =
    useState([]);

  const [selectedModules, setSelectedModules] =
    useState([]);

  const [search, setSearch] = useState({
    text: '',
    triggerId: Date.now()
  });

  const [deleteopen, setDeleteOpen] =
    useState(false);

  const [deleteData, setDeleteData] =
    useState(null);

  const [documentNameCreatePermission,
    setDocumentNameCreatePermission] =
    useState(false);

  const [documentNameUpdatePermission,
    setDocumentNameUpdatePermission] =
    useState(false);

  const [documentNameDeletePermission,
    setDocumentNameDeletePermission] =
    useState(false);

  const [showSnackBarAlert,
    setShowSnackBarAlert] = useState({
    status: false,
    type: 'success',
    message: null
  });

  const moduleOptions = [
    'RFQ',
    'PlacementSlip',
    'Policy',
    'Claims',
    'Endorsement'
  ];

  useEffect(() => {
    getDocumentNameList();
  }, [
    rowsPerPage,
    offset,
    search,
    selectedModules
  ]);

  useEffect(() => {
    checkCreatePermission();
    checkUpdatePermission();
    checkDeletePermission();
  }, []);

  const checkCreatePermission =
    async () => {
      await checkPermission(
        'documentName',
        'Create'
      ).then((permission) => {
        setDocumentNameCreatePermission(
          permission
        );
      });
    };

  const checkUpdatePermission =
    async () => {
      await checkPermission(
        'documentName',
        'Update'
      ).then((permission) => {
        setDocumentNameUpdatePermission(
          permission
        );
      });
    };

  const checkDeletePermission =
    async () => {
      await checkPermission(
        'documentName',
        'Delete'
      ).then((permission) => {
        setDocumentNameDeletePermission(
          permission
        );
      });
    };

  const getDocumentNameList =
    async () => {
      setLoading(true);

      request({
        url:
          'documentName/getDocumentNameList',
        method: 'POST',
        data: {
          search: search?.text,
          modules: selectedModules,
          offset,
          limit: rowsPerPage,
          orderColumn: 'id',
          orderDirection: 'DESC'
        }
      }).then(
        (response:any) => {
          setDocumentNameList(
            response?.['documentNameData'] || []
          );

          setTotalCount(
            response?.['documentNameCount'] || 0
          );

          setLoading(false);
        },
        (error) => {
          console.log(error);
          setLoading(false);
        }
      );
    };

  const changeSearch = (searchValue) => {
    setOffset(0);
    setSearch(searchValue);
  };

  const handleChangePage = (
    event,
    newPage
  ) => {
    setOffset(newPage);
  };

  const handleChangeRowsPerPage = (
    event
  ) => {
    setRowsPerPage(
      parseInt(event.target.value, 10)
    );
    setOffset(0);
  };

  const handleStatusChange =
    async (row) => {
      try {
        const response = await request({
          url:
            'documentName/statusUpdate',
          method: 'POST',
          data: {
            id: row.id,
            status: !row.status
          }
        });

        if (response.status) {
          setShowSnackBarAlert({
            status: true,
            type: 'success',
            message: 'Status updated'
          });

          getDocumentNameList();
        }
      } catch (error) {
        setShowSnackBarAlert({
          status: true,
          type: 'error',
          message: error.message
        });
      }
    };

  const handleDeleteOpen = (
    documentName
  ) => {
    setDeleteData(documentName);
    setDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteOpen(false);
    setDeleteData(null);
    getDocumentNameList();
  };

  const handleSnackbarClose = () => {
    setShowSnackBarAlert({
      status: false,
      type: null,
      message: null
    });
  };

  return (
    <>
      <SnackbarAlert
        showSnackBarAlert={
          showSnackBarAlert
        }
        handleSnackbarClose={
          handleSnackbarClose
        }
      />

      <Card>
        <Grid
          container
          spacing={2}
          mt={1}
          mb={1}
          mr={1}
          ml={1}
        >
          <Grid item xs={12} md={4}>
            <PageHeader
              title="Document Names"
              button_text="Create"
              showCreateEditForm={
                showCreateEditForm
              }
              createPermission={
                documentNameCreatePermission
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TableSearchWithIcon
              changeSearch={changeSearch}
              deafultSearchValue={
                search?.text
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Autocomplete
              multiple
              size="small"
              options={moduleOptions}
              value={selectedModules}
              onChange={(
                event,
                value
              ) => {
                setSelectedModules(
                  value
                );
                setOffset(0);
              }}
              renderTags={(
                value,
                getTagProps
              ) =>
                value.map(
                  (option, index) => (
                    <Chip
                      label={option}
                      size="small"
                      {...getTagProps({
                        index
                      })}
                    />
                  )
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Modules"
                  size="small"
                />
              )}
            />
          </Grid>
        </Grid>

        <Divider />

        {isLoading ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">
                    <img
                      alt="loader"
                      src={loader}
                      width={100}
                      height={100}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
        ) : (
          <TableContainer>
            {documentNameList?.length >
            0 ? (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      Document Name
                    </TableCell>

                    <TableCell>
                      Modules
                    </TableCell>

                    <TableCell align="center">
                      Status
                    </TableCell>

                    <TableCell>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {documentNameList.map(
                    (row) => (
                      <TableRow
                        hover
                        key={row.id}
                      >
                        <TableCell>
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                          >
                            {row.name}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Box
                            sx={{
                              display:
                                'flex',
                              flexWrap:
                                'wrap',
                              gap: 0.5
                            }}
                          >
                            {row.modules?.map(
                              (
                                module
                              ) => (
                                <Chip
                                  key={
                                    module
                                  }
                                  label={
                                    module
                                  }
                                  size="small"
                                />
                              )
                            )}
                          </Box>
                        </TableCell>

                        <TableCell align="center">
                          <Switch
                            checked={
                              row.status
                            }
                            onChange={() =>
                              handleStatusChange(
                                row
                              )
                            }
                          />
                        </TableCell>

                        <TableCell>
                          {documentNameUpdatePermission && (
                            <Tooltip
                              title="Edit"
                              arrow
                            >
                              <IconButton
                                sx={{
                                  '&:hover':
                                    {
                                      background:
                                        theme
                                          .colors
                                          .primary
                                          .lighter
                                    },
                                  color:
                                    theme
                                      .palette
                                      .primary
                                      .main
                                }}
                                size="small"
                                onClick={() =>
                                  editDatas(
                                    row
                                  )
                                }
                              >
                                <EditTwoToneIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                         

                          {documentNameDeletePermission && (
                            <Tooltip
                              title="Delete"
                              arrow
                            >
                              <IconButton
                                sx={{
                                  '&:hover':
                                    {
                                      background:
                                        theme
                                          .colors
                                          .error
                                          .lighter
                                    },
                                  color:
                                    theme
                                      .palette
                                      .error
                                      .main
                                }}
                                size="small"
                                onClick={() =>
                                  handleDeleteOpen(
                                    row
                                  )
                                }
                              >
                                <DeleteTwoToneIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            ) : (
              <NoDataAvailable />
            )}
          </TableContainer>
        )}

        <Box p={2}>
          <TablePagination
            rowsPerPageOptions={[
              25,
              50,
              75,
              100
            ]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={offset}
            onPageChange={
              handleChangePage
            }
            onRowsPerPageChange={
              handleChangeRowsPerPage
            }
          />
        </Box>

        <DeleteAlert
          deleteopen={deleteopen}
          deleteData={deleteData}
          closeDeleteModal={
            closeDeleteModal
          }
        />
      </Card>
    </>
  );
};

export default DocumentNameList;